import { Link, useParams } from 'react-router-dom';
import { news } from '@/mocks/news';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function NewsDetail() {
  const { id } = useParams();
  const item = news.find((n) => n.id === id);

  if (!item) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-20 md:py-32 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
          <i className="ri-newspaper-line text-3xl"></i>
        </span>
        <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950">未找到该资讯</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的资讯可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = item.relatedIds
    .map((rid) => news.find((n) => n.id === rid))
    .filter((n): n is (typeof news)[number] => Boolean(n) && n!.id !== item.id)
    .slice(0, 5);

  return (
    <article className="w-full">
      <div className="max-w-content mx-auto px-4 md:px-6 pt-6 pb-2">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '影视资讯', to: '/news' },
            { label: item.title },
          ]}
        />
      </div>

      <header className="max-w-3xl mx-auto px-4 md:px-6 pt-4 pb-8">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
          <i className="ri-price-tag-3-line"></i>
          {item.category}
        </span>
        <h1 className="mt-4 text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
          {item.title}
        </h1>
        <div className="mt-5 flex items-center gap-3 text-sm text-foreground-500 flex-wrap">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
            <span className="tabular-nums">{item.date}</span>
          </span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-foreground-100">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 影视资讯配图`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="space-y-5">
          {item.content.map((para, idx) => {
            if (para.startsWith('## ')) {
              return (
                <h2
                  key={idx}
                  className="pt-3 text-lg md:text-xl font-bold text-foreground-950"
                >
                  {para.replace('## ', '')}
                </h2>
              );
            }
            return (
              <p key={idx} className="text-base leading-8 text-foreground-700">
                {para}
              </p>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {item.tags.map((t) => (
            <span
              key={t}
              className="px-3 py-1 rounded-md bg-background-100 text-foreground-600 text-xs border border-background-200"
            >
              #{t}
            </span>
          ))}
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-background-100 border-t border-background-200">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-12">
            <div className="flex items-center gap-3">
              <span className="w-1.5 h-6 rounded-full bg-accent-500"></span>
              <h2 className="text-xl font-bold text-foreground-950">相关文章</h2>
            </div>
            <ul className="mt-6 space-y-4">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/news/detail/${r.id}`}
                    className="group flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                  >
                    <i className="ri-file-text-line text-primary-500 w-5 h-5 flex items-center justify-center shrink-0"></i>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {r.title}
                      </span>
                      <span className="block mt-1 text-xs text-foreground-400 tabular-nums">
                        {r.date} · {r.category}
                      </span>
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-300 w-5 h-5 flex items-center justify-center shrink-0"></i>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </article>
  );
}